/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { CSSProperties, FC, forwardRef, SVGProps, useEffect, useRef } from 'react';
import { Box, SxProps } from '@mui/material';
import { CloseRounded } from '@mui/icons-material';
import QSIconButton from '../../atoms/icon-button';
import TextInput from '../text-input';
import SearchIcon from '../../../icons/icons-revamp/search';
import { HEADER_HEIGHT } from '../../../typings';

interface SearchBarProps {
  value?: string;
  isProduct?: boolean;
  isCatalogue?: boolean;
  onChange: (value: string) => void;
  onClose?: () => void;
  containerCSS?: CSSProperties;
  placeholder?: string;
  closeIconSx?: SxProps;
  debounceTime?: number;
  showClose?: boolean;
  showSearchIcon?: boolean;
  searchIconProps?: { size: number; color: string };
}

const QSSearchBar = forwardRef<HTMLDivElement, SearchBarProps>((
  {
    value,
    onChange,
    isProduct = false,
    isCatalogue = false,
    onClose,
    placeholder,
    showClose,
    containerCSS = {},
    closeIconSx = {},
    debounceTime = 0,
    showSearchIcon = false,
    searchIconProps,
  },
  ref
) => {
    return (
      <div
        ref={ref}
        style={containerCSS}
        css={css`
          height: calc(${HEADER_HEIGHT} - 1px);
          display: flex;
          width: 100%;
          justify-content: space-between;
          align-items: center;
        `}
      >
        {showSearchIcon && (
          <Box
            sx={{
              marginRight: '8px',
              display: 'flex',
              marginBottom: '2px',
              alignItems: 'center',
            }}
          >
            <SearchIcon {...searchIconProps} />
          </Box>
        )}
        <TextInput
          value={value}
          textInputProps={{
            autoFocus: true,
          }}
          autoComplete="off"
          autoFocus={true}
          sx={{
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            '.MuiInputBase-input': {
              fontSize: '14px !important',
              fontWeight: '500 !important',
            },
            gap: '0px !important',
            '.MuiFormControl-root': {
              margin: '0px',
            },
            '.MuiInputBase-root::before': {
              borderBottom: 'none !important',
            },
            '.MuiInputBase-root,.MuiInput-root': {
              '&::before': {
                border: 'none',
              },
              '&::after': {
                border: 'none',
              },
              '&:hover:not(.Mui-disabled)::before': {
                border: 'none',
              },
              'input::placeholder, textArea::placeholder': {
                color: '#7A7A7A',
              },
            },
          }}
          onChange={(event) => {
            onChange(event.target.value);
          }}
          placeholder={typeof placeholder === 'string' ? placeholder : 'Search...'}
        />
        {showClose && (
          <QSIconButton sx={closeIconSx} onClick={onClose}>
            <CloseRounded
              sx={{
                width: '20px',
                height: '20px',
              }}
            />
          </QSIconButton>
        )}
      </div>
    );
  }
);

export default QSSearchBar;
